import { PlayerEvents } from "../player/player-events";
import { PlayerTracker } from "@nrk/player-tracker";
import { getElementOfType } from "../utils/dom";
import { getLogger } from "../utils/logging";
import {
  ReactiveElement,
  ReactiveState
} from "@nrk/reactive-element";
import SpringStreamsClass from "@nrk/player-tracker/vendor/springstreams";
class TrackingElement extends ReactiveElement {
  static get reactiveAttributes() {
    return {
      ...super.reactiveAttributes,
      gaTrackingId: String,
      gaAppId: String
    };
  }
  static get reactiveProperties() {
    return {
      ...super.reactiveProperties,
      activeSession: Object,
      autoplay: Boolean,
      epgState: ReactiveState,
      user: Object
    };
  }
  player;
  playerTracker;
  trackingSession;
  sourceId;
  activeSession;
  autoplay;
  epgState;
  user;
  gaTrackingId = "";
  gaAppId = "";
  _logger = getLogger("TrackingElement");
  _unshadowProperties;
  readyCallback() {
    super.readyCallback();
    const gaAppId = this.gaAppId;
    const gaTrackingId = this.gaTrackingId;
    if (!gaTrackingId) {
      throw new Error("Missing attribute 'gatrackingid' which is required for tracking");
    }
    this.sourceId = this.getAttribute("source-id") || void 0;
    const player = getElementOfType(this);
    this.player = player;
    this.playerTracker = new PlayerTracker(
      {
        name: this.player.playerName,
        version: this.player.playerVersionNumber
      },
      {
        gaTrackingId,
        gaAppId,
        scoresLibrary: SpringStreamsClass
      }
    );
    player.addEventListener(PlayerEvents.SessionCreated, this);
    player.addEventListener(PlayerEvents.PlayNextEpisode, this);
    player.addEventListener(PlayerEvents.TrackQoS, this);
    this._unshadowProperties = this.player.shadowProperties(["activeSession", "autoplay", "epgState", "user"], this);
  }
  disconnectedCallback() {
    this.player?.removeEventListener(PlayerEvents.SessionCreated, this);
    this.player?.removeEventListener(PlayerEvents.PlayNextEpisode, this);
    this.player?.removeEventListener(PlayerEvents.TrackQoS, this);
    this.playerTracker?.destroy();
    this.playerTracker = void 0;
    this.player = void 0;
    this.trackingSession = void 0;
    this._unshadowProperties?.();
    super.disconnectedCallback();
  }
  updating(propName, oldValue) {
    super.updating(propName, oldValue);
    if (propName === "epgState" && oldValue !== void 0) {
      oldValue.events.off("programchange", this.trackProgramChange, this);
    }
  }
  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has("activeSession")) {
      if (this.trackingSession !== void 0) {
        this.trackingSession = void 0;
      }
      if (this.activeSession !== void 0) {
        this.trackingSession = this.createTrackingSession(this.activeSession);
      }
    }
    if (changedProperties.has("epgState") && this.epgState !== void 0) {
      this.epgState.events.on("programchange", this.trackProgramChange, this);
    }
    if (changedProperties.has("autoplay")) {
      this.trackingSession?.gaSender?.updateFields({ autoplay: this.autoplay });
    }
  }
  handleEvent(event) {
    if (event.type === PlayerEvents.PlayNextEpisode) {
      if (this.playerTracker && this.playerTracker.progressTracker) {
        this.playerTracker.progressTracker.finalizeProgressReporting(this.activeSession?.getMediaRange().end);
      }
    }
    if (event.type === PlayerEvents.TrackQoS) {
      const castedEvent = event;
      let urlParts = { mediaUrlHost: "", mediaUrlPath: "", mediaUrlQuery: "" };
      if (castedEvent.detail.mediaUrl) {
        const url = new URL(castedEvent.detail.mediaUrl);
        urlParts = { mediaUrlHost: url.origin, mediaUrlPath: url.pathname, mediaUrlQuery: url.search };
      }
      const properties = { ...castedEvent.detail, ...urlParts };
      this.playerTracker?.appTracker.trackQoSEvent({ properties });
    }
  }
  createTrackingSession(playbackSession) {
    const { epgState, player, playerTracker } = this;
    const { psapiClient, metadata, manifest } = this.player ?? {};
    if (!manifest || !metadata || !player || !playerTracker || !psapiClient) {
      const props = { manifest, metadata, player, playerTracker, psapiClient };
      const missing = Object.keys(props).filter((key) => props[key] === void 0);
      this._logger.log(`Tracking needs: ${missing.join(", ")}`);
      return;
    }
    if (!("playable" in manifest)) {
      this._logger.log("Tracking only applies to playable media.");
      return;
    }
    const epgEvents = epgState?.events;
    if (metadata.isChannel && !epgEvents) {
      this._logger.log("Tracking needs epgState.events (media is channel)");
      return;
    }
    this._logger.log("Creating tracking session.");
    const sessionConfig = {
      metadata: {
        mediaId: metadata.id ?? "unknown",
        mediaTitle: metadata.heading ?? "unknown",
        cdnName: manifest.statistics.luna.data.cdnName
      },
      ga: {
        accumulatedTimeEvents: [120],
        fields: {
          sourceId: this.sourceId,
          autoplay: this.autoplay,
          userId: this.user?.id,
          fields: {
            eventLabel: metadata.heading,
            ...manifest.statistics.ga
          }
        }
      },
      ecommerce: {
        tracker: {
          sendCompleteEvent: () => this.trackEcommerceEvent(PlayerEvents.EcommerceConsumeComplete),
          sendStartEvent: () => this.trackEcommerceEvent(PlayerEvents.EcommerceConsumeStart)
        }
      },
      scores: {
        config: manifest.statistics.scores
      },
      akamai: {
        config: manifest.statistics.luna
      }
    };
    if (metadata.id) {
      sessionConfig.progress = {
        trackingCallback: this.trackProgress.bind(this),
        programId: metadata.id,
        playbackStartTime: player.startTimeOndemand ?? 0
      };
    }
    return playerTracker.trackSession(playbackSession, psapiClient, sessionConfig, epgEvents);
  }
  trackEcommerceEvent(eventName) {
    this.dispatchEvent(new CustomEvent(eventName, { bubbles: true, detail: eventName }));
  }
  trackProgramChange(epgEntry) {
    this.dispatchEvent(new CustomEvent(PlayerEvents.EPGChange, { bubbles: true, detail: epgEntry }));
  }
  trackProgress(progress) {
    this.dispatchEvent(new CustomEvent(PlayerEvents.TrackProgress, { bubbles: true, detail: progress }));
  }
}
export {
  TrackingElement
};
